import React, { useRef } from "react";

import Typography from "@mui/material/Typography";

import useRssLinksSettings from "@app/analytics/hooks/settings/useRssLinksSettings";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import * as Events from "@app/analytics/tracking/events";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import type { Integration } from "@app/common/types";

import Alert from "@rui/Alert";
import ConfirmationModal, {
  ConfirmationModalProps,
} from "@rui/ConfirmationModal";
import { Link } from "@rui/Link";
import { Row } from "@rui/Stack";
import Switch from "@rui/Switch";

interface DisconnectCalendarConfirmationModalProps
  extends Omit<ConfirmationModalProps, "children"> {
  calendar: Integration;
}

const DisconnectCalendarConfirmationModal = ({
  calendar,
  ...modalProps
}: DisconnectCalendarConfirmationModalProps) => {
  const { userSettings } = useUserSettings();
  const { rssCalendar, rssLinkSettings, updateRssLinkSettings } =
    useRssLinksSettings();

  const rssEnabled = rssLinkSettings.isEnabled;
  const isRssCalendar = calendar.providerId === rssCalendar?.provider;

  // Keeps the toggle from disappearing when the user toggles rss off
  // and also allows us to not show it in the first place if rss is off to
  // begin with
  const showRssToggle = useRef(isRssCalendar && rssEnabled);

  const shouldUpsellManualJoin =
    userSettings.calendarPermissionLevel !== "invite_only";

  const handleToggleRssLinks = (event: React.ChangeEvent, enable: boolean) => {
    enable ? Events.enableRssLinks() : Events.disableRssLinks();
    updateRssLinkSettings.mutate(
      { isEnabled: enable },
      {
        onError: () => {
          toast.error(
            `Unable to ${
              enable ? "enable" : "disable"
            } Smart Scheduler links. Please try again via the Smart Scheduler settings.`
          );
        },
      }
    );
  };

  return (
    <ConfirmationModal
      {...modalProps}
      title="Disconnect calendar?"
      cancelProps={{ label: "Nevermind" }}
      confirmProps={{
        type: "negative",
        label: "Yes, disconnect calendar",
        disabled: isRssCalendar && rssEnabled,
        tooltip:
          "Please turn off Read Smart Scheduler to disconnect this calendar",
      }}
      inverse
      size="md"
    >
      <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
        Without a connected calendar, Read will miss meetings and Meeting
        Reports will be less accurate.
      </Typography>
      {shouldUpsellManualJoin && (
        <Alert severity="info" embedded>
          <Typography variant="bodySecondaryReg" color="shades.neutral.600">
            Instead of disconnecting, you can update your join preferences to{" "}
            <Link
              to={ROUTES.USER_SETTINGS_JOIN_PREFERENCES}
              underline
              onClick={modalProps.onClose}
            >
              manually select meetings Read joins
            </Link>
            .
          </Typography>
        </Alert>
      )}
      {showRssToggle.current && (
        <Row spacing={2}>
          <Typography variant="bodySecondaryReg" color="shades.neutral.600">
            This calendar is in use by Smart Scheduler. If you want to
            disconnect this calendar, you must first disable your Smart
            Scheduler links.
          </Typography>
          <Switch
            withStatusLabel
            checked={rssEnabled}
            onChange={handleToggleRssLinks}
          />
        </Row>
      )}
    </ConfirmationModal>
  );
};

export default DisconnectCalendarConfirmationModal;
